"use client";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";

import DateSelector from "../components/DateSelector";
import { useBookingDate } from "../components/DateSelector/useBookingDate";
import DisplaySchedules from "./Openings";
import { formatDate, getDatesBetween } from "./utils";
import { DB_DATE_FORMAT } from "@utils/core";
import { filterQuoteServicesForSchedule } from "@openings/booking-core";
import type { DbDateString, UserScheduleDTO } from "@openings/api-client";
import { CloseOutlined, LeftOutlined } from "@ant-design/icons";
import Button from "antd/es/button";
import SelectService from "../components/SelectService";
import { LocationStep } from "../LocationStep";
import type {
  BookingAppointmentForm,
  BookingUserAppointmentProps,
  SelectedService,
} from "../types";
import {
  bookingColumnStyle,
  bookingPageTitleStyle,
  bookingSmallStyle,
  bookingStackGapStyle,
  selectedLocationSummaryStyle,
  selectedLocationCopyStyle,
  locationChangeButtonStyle,
  appointmentHeaderStyle,
  appointmentHeaderTitleStyle,
} from "../bookingStyles";
import { useDrawerBack } from "../MobileBookingDrawer";
import { DesktopBookingDrawer } from "../DesktopBookingDrawer";
import { bookingTimeZoneFor } from "../bookingTimeZone";
import { resolveCatalogRequirementLabel } from "@openings/booking-core";

const MemberBooking = ({
  salonId,
  user,
  teamMemberId,
  services,
  schedules,
  vacations,
  dateLabel = "Appointment",
  serviceLabel,
  showLocationSummary = false,
  showAvailabilityStatus = false,
  cancellationPolicyEnabled,
  cancellationPolicyHours,
  cancellationPolicyNote,
  lateGraceMinutes,
  verificationMethod,
  bookingHorizonDays,
  bookingTimeZone,
  openingsClient,
  openAppointmentForm,
  submitServiceRequest,
  uploadMedia,
  onBack,
  renderInlineAppointment,
}: BookingUserAppointmentProps) => {
  // A location offers only the services its business schedule assigns
  // (#829). The API sends them as `assignedServiceIds`, empty meaning every
  // service, and refuses a booking outside them. A location that offers none
  // of this member's services has nothing to book here.
  const servicesAt = useCallback(
    (schedule: UserScheduleDTO) =>
      filterQuoteServicesForSchedule(
        services,
        schedule.assignedServiceIds ?? [],
      ),
    [services],
  );
  const bookableSchedules = useMemo(
    () =>
      services.length === 0
        ? schedules
        : schedules.filter((schedule) => servicesAt(schedule).length > 0),
    [schedules, services.length, servicesAt],
  );

  const hasMultipleSchedules = bookableSchedules.length > 1;
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(
    () =>
      showLocationSummary && bookableSchedules.length === 1
        ? bookableSchedules[0].id
        : null,
  );

  useEffect(() => {
    if (!showLocationSummary) return;

    setSelectedScheduleId((current) => {
      if (
        current &&
        bookableSchedules.some((schedule) => schedule.id === current)
      ) {
        return current;
      }
      return bookableSchedules.length === 1 ? bookableSchedules[0].id : null;
    });
  }, [bookableSchedules, showLocationSummary]);

  const selectedSchedule = useMemo(
    () =>
      bookableSchedules.find((schedule) => schedule.id === selectedScheduleId) ??
      null,
    [bookableSchedules, selectedScheduleId],
  );

  // With a location chosen first, its own services; with every location
  // listed at once, the services at least one of them offers.
  const offeredServices = useMemo(() => {
    if (showLocationSummary) {
      return selectedSchedule ? servicesAt(selectedSchedule) : services;
    }
    const offered = new Set(
      bookableSchedules.flatMap((schedule) =>
        servicesAt(schedule).map((service) => service.id),
      ),
    );
    return services.filter((service) => offered.has(service.id));
  }, [
    bookableSchedules,
    selectedSchedule,
    services,
    servicesAt,
    showLocationSummary,
  ]);

  const [selectedServices, setSelectedServices] = useState<SelectedService[]>(
    () => (offeredServices[0] ? [{ ...offeredServices[0] }] : []),
  );

  useEffect(() => {
    setSelectedServices(
      offeredServices[0] ? [{ ...offeredServices[0] }] : [],
    );
  }, [offeredServices]);

  const activeSchedules = useMemo(() => {
    if (showLocationSummary) return selectedSchedule ? [selectedSchedule] : [];
    // Every location is listed at once, so show only those offering the
    // whole selection.
    return bookableSchedules.filter(
      (schedule) =>
        filterQuoteServicesForSchedule(
          selectedServices,
          schedule.assignedServiceIds ?? [],
        ).length === selectedServices.length,
    );
  }, [bookableSchedules, selectedSchedule, selectedServices, showLocationSummary]);

  // On the location's calendar, the one the openings API reads `date` in
  // (BF-DEC-015).
  const dateTimeZone = bookingTimeZoneFor(
    selectedSchedule ??
      (bookableSchedules.length === 1 ? bookableSchedules[0] : null),
    bookingTimeZone,
  );
  const [date, setDate] = useBookingDate(dateTimeZone);
  const dateString = formatDate(date);

  const [refreshCount, setRefreshCount] = useState(0);

  // Inline appointment flow — same pattern as ScheduleBooking: `desktop` is
  // the layout when the flow opened, kept until it closes so crossing 768px
  // does not remount the flow and drop its state (BF-DRIFT-021).
  const [inlineAppointment, setInlineAppointment] = useState<{
    form: BookingAppointmentForm;
    desktop: boolean;
  } | null>(null);
  const inlineAppointmentForm = inlineAppointment?.form ?? null;

  // Detect desktop vs mobile for inline flow mode
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 768px)");
    setIsDesktop(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  const handleOpenAppointmentForm = useCallback(
    (form: BookingAppointmentForm) => {
      if (renderInlineAppointment) {
        setInlineAppointment({ form, desktop: isDesktop });
      } else {
        openAppointmentForm?.(form);
      }
    },
    [isDesktop, openAppointmentForm, renderInlineAppointment],
  );

  const handleCloseInlineFlow = useCallback(() => {
    setInlineAppointment(null);
    setRefreshCount((c) => c + 1);
  }, []);

  // Register back handler in the drawer header
  const { setDrawerBack } = useDrawerBack();
  useEffect(() => {
    if (inlineAppointmentForm) {
      setDrawerBack(handleCloseInlineFlow);
    } else {
      setDrawerBack(null);
    }
    return () => setDrawerBack(null);
  }, [inlineAppointmentForm, handleCloseInlineFlow, setDrawerBack]);

  // The member's own services carry their effective terms, so the selection
  // states its exact request/payment requirement before a time is chosen
  // (BF-DRIFT-005).
  const selectionRequirementNote = useMemo(
    () =>
      resolveCatalogRequirementLabel(selectedServices, { exactTerms: true }),
    [selectedServices],
  );

  const workDays = useMemo<number[]>(() => {
    const days: number[] = [];

    activeSchedules.forEach(({ hours }) => {
      hours.forEach((hour, index: number) => {
        if (hour.isOpen === true && !days.includes(index)) {
          days.push(index);
        }
      });
    });

    return days;
  }, [activeSchedules]);

  // Every time-off type is unbookable, so all of them grey out on the
  // calendar.
  const convertedVacations = useMemo(() => {
    return vacations
      ? vacations.flatMap((vacation) =>
          getDatesBetween(
            vacation.startDate as DbDateString,
            vacation.endDate as DbDateString,
          ),
        )
      : [];
  }, [vacations]);

  // …but only a VACATION is announced. A DAY_OFF should read as an ordinary
  // closed day, with no "away" messaging. `type` is optional on the DTO for
  // clients predating the field; those rows default to VACATION.
  const announcedVacationDates = useMemo(() => {
    return vacations
      ? vacations
          .filter((vacation) => (vacation.type ?? "VACATION") === "VACATION")
          .flatMap((vacation) =>
            getDatesBetween(
              vacation.startDate as DbDateString,
              vacation.endDate as DbDateString,
            ),
          )
      : [];
  }, [vacations]);

  const isTodayOnVacation = useMemo(
    () =>
      announcedVacationDates.some(
        (_date) => dayjs(_date).format(DB_DATE_FORMAT) === dateString,
      ),
    [announcedVacationDates, dateString],
  );

  const filteredWorkDays = useMemo(
    () => {
      // When every regular day is closed, keep all days selectable so
      // one-off overrides (extra hours) remain discoverable.
      if (workDays.length === 0) {
        return [];
      }
      return [0, 1, 2, 3, 4, 5, 6].filter((day) => !workDays.includes(day));
    },
    [workDays],
  );

  const appointmentHeader = (
    <section aria-labelledby="appointment-section">
      {/* Title */}
      <div
        className="openings-appointment-header"
        style={appointmentHeaderStyle}
      >
        {onBack && (
          <Button
            type="text"
            size="small"
            icon={<LeftOutlined />}
            onClick={onBack}
          >
            Back
          </Button>
        )}
      </div>
    </section>
  );

  const datePickerSection = (
    <section aria-labelledby="date-picker-section">
      <div style={bookingColumnStyle}>
        <DateSelector
          label={showAvailabilityStatus ? undefined : dateLabel}
          descriptiveInputLabel={showAvailabilityStatus}
          selectedDate={date}
          setDate={setDate}
          filteredWorkDays={filteredWorkDays}
          excludedDates={convertedVacations}
          hasBorder={true}
          neutralPalette={showAvailabilityStatus}
          bookingHorizonDays={bookingHorizonDays}
          timeZone={dateTimeZone}
        />
      </div>
    </section>
  );

  const schedulePanel = (
    <section aria-labelledby="schedule-display-section">
      <DisplaySchedules
        salonId={salonId}
        user={user}
        teamMemberId={teamMemberId}
        schedules={activeSchedules}
        isTodayOnVacation={isTodayOnVacation}
        date={dateString}
        selectedService={selectedServices}
        refreshCount={refreshCount}
        onRefresh={() => setRefreshCount((c) => c + 1)}
        cancellationPolicyEnabled={cancellationPolicyEnabled}
        cancellationPolicyHours={cancellationPolicyHours}
        cancellationPolicyNote={cancellationPolicyNote}
        lateGraceMinutes={lateGraceMinutes}
        verificationMethod={verificationMethod}
        openingsClient={openingsClient}
        openAppointmentForm={handleOpenAppointmentForm}
        submitServiceRequest={submitServiceRequest}
        uploadMedia={uploadMedia}
        showAvailabilityStatus={showAvailabilityStatus}
      />
    </section>
  );

  // Mobile inline appointment flow (replaces booking UI inside MobileBookingDrawer)
  if (
    inlineAppointment &&
    renderInlineAppointment &&
    !inlineAppointment.desktop
  ) {
    return (
      <div
        className="openings-member-inline-appointment"
        style={bookingStackGapStyle}
      >
        <div
          className="openings-appointment-header"
          style={appointmentHeaderStyle}
        >
          <span
            style={{
              ...appointmentHeaderTitleStyle,
              fontSize: "1em",
              lineHeight: 1.4,
            }}
          >
            Book an appointment
          </span>
          <Button
            color="default"
            variant="filled"
            icon={<CloseOutlined />}
            aria-label="Close appointment"
            onClick={handleCloseInlineFlow}
          />
        </div>
        {renderInlineAppointment(inlineAppointment.form, handleCloseInlineFlow)}
      </div>
    );
  }

  if (
    showLocationSummary &&
    hasMultipleSchedules &&
    !selectedScheduleId
  ) {
    return (
      <div style={bookingStackGapStyle}>
        {onBack && appointmentHeader}
        <LocationStep
          schedules={bookableSchedules.map((schedule) => ({
            id: schedule.id,
            title: schedule.title,
            address: schedule.address ?? undefined,
          }))}
          onSelect={setSelectedScheduleId}
        />
      </div>
    );
  }

  return (
    <div style={bookingStackGapStyle}>
      <style>{`
        .openings-member-location-change-button:focus-visible {
          outline: 2px solid var(--openings-color-text, #111);
          outline-offset: 2px;
        }
      `}</style>
      {onBack && appointmentHeader}
      {showLocationSummary && selectedSchedule ? (
        <div
          className="openings-selected-location"
          style={selectedLocationSummaryStyle}
        >
          <div
            className="openings-selected-location-copy"
            style={selectedLocationCopyStyle}
          >
            <span
              className="openings-selected-location-title"
              style={bookingPageTitleStyle}
            >
              {selectedSchedule.title}
            </span>
            {selectedSchedule.address ? (
              <span
                className="openings-selected-location-address"
                style={bookingSmallStyle}
              >
                {selectedSchedule.address}
              </span>
            ) : null}
          </div>
          {hasMultipleSchedules ? (
            <button
              type="button"
              className="openings-member-location-change-button"
              onClick={() => setSelectedScheduleId(null)}
              style={locationChangeButtonStyle}
            >
              Change
            </button>
          ) : null}
        </div>
      ) : null}
      {datePickerSection}
      {offeredServices.length > 0 && (
        <section aria-labelledby="service-selection-section">
          <SelectService
            showPrice={true}
            exactTerms={true}
            label={serviceLabel}
            defaultServices={offeredServices}
            selectedServices={selectedServices}
            setServices={setSelectedServices}
            variant="compact"
            inheritPopupTheme={showAvailabilityStatus}
          />
          {selectionRequirementNote && (
            <p
              className="openings-selection-requirement"
              style={{
                ...bookingSmallStyle,
                margin: "var(--openings-booking-spacing-sm, 8px) 0 0",
              }}
            >
              {selectionRequirementNote}
            </p>
          )}
        </section>
      )}

      {schedulePanel}

      {/* Desktop appointment drawer overlay */}
      {inlineAppointment &&
        renderInlineAppointment &&
        inlineAppointment.desktop && (
        <DesktopBookingDrawer open={true} onClose={handleCloseInlineFlow}>
          {renderInlineAppointment(
            inlineAppointment.form,
            handleCloseInlineFlow,
          )}
        </DesktopBookingDrawer>
      )}
    </div>
  );
};

export default React.memo(MemberBooking);
